/*
 * MPS client-side catalog filters (search + collection pages).
 *
 * Loads every product in the current collection/search from the
 * `?view=mps-data` feed in batches of 250 (first batch immediately, the
 * rest in the background), then filters, counts, sorts and paginates in
 * the browser. The server-rendered results and Shopify's native filter
 * form stay in the page as the fallback until the first batch arrives.
 *
 * Markup contract (see sections/mps-search.liquid, mps-collection.liquid):
 *   [data-mps-catalog]   root; data-mode="grid|list", data-per-page,
 *                        data-terms, data-money-format, data-phone-href,
 *                        data-empty-url
 *   [data-mps-facets]    client filter sidebar (hidden until ready)
 *   [data-mps-native]    native filter form + chips (hidden once ready)
 *   [data-mps-results]   results container (server HTML replaced)
 *   [data-mps-count]     "Showing x–y of N"
 *   [data-mps-sort]      sort <select>
 *   [data-mps-chips]     active filter chips
 *   [data-mps-pager]     pagination
 *   [data-mps-view]      optional grid/list toggle buttons (data-mps-view="grid|list")
 */
(() => {
  const root = document.querySelector('[data-mps-catalog]');
  if (!root || !window.fetch || !window.URLSearchParams) return;

  const $ = sel => root.querySelector(sel);
  const cfg = root.dataset;
  const els = {
    facets: $('[data-mps-facets]'),
    native: root.querySelectorAll('[data-mps-native]'),
    results: $('[data-mps-results]'),
    count: $('[data-mps-count]'),
    sort: $('[data-mps-sort]'),
    chips: $('[data-mps-chips]'),
    pager: $('[data-mps-pager]'),
  };
  if (!els.results || !els.facets) return;
  // Hide the native filters while the feed loads (restored if loading fails)
  root.classList.add('mps-loading');
  els.facets.hidden = false;
  els.facets.innerHTML = '<h3>Refine results</h3><p class="filter-hint">Loading filters…</p>';

  const PER_PAGE = parseInt(cfg.perPage, 10) || 24;
  const BATCH_PARAM = 'view';
  const P = 'mf.'; // URL param prefix, kept apart from Shopify's own filter params

  // ---------- helpers ---------------------------------------------------------
  const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const FRAC = { '¼': '.25', '½': '.5', '¾': '.75' };
  const hpNum = v => parseFloat(String(v).replace(/(\d)?([¼½¾])/g, (m, d, f) => (d || '0') + FRAC[f]).replace(/[^\d.]/g, '')) || 0;
  const bucket = (n, edges) => {
    if (n === null || n === undefined || n === '') return null;
    const i = edges.findIndex(([lo, hi]) => n >= lo && n < hi);
    return i < 0 ? null : edges[i][2];
  };
  const formatMoney = (cents, trim) => {
    const fmt = cfg.moneyFormat || '${{amount}}';
    const amt = (cents / 100).toFixed(2);
    const withCommas = n => n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const [whole, dec] = amt.split('.');
    let amount = withCommas(whole) + (trim && dec === '00' ? '' : '.' + dec);
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) =>
      k === 'amount_no_decimals' ? withCommas(whole) :
      k === 'amount_with_comma_separator' ? amount.replace('.', ',') : amount);
  };
  const terms = (cfg.terms || '').trim().split(/\s+/).filter(t => t.length > 1).map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const highlight = s => {
    const e = esc(s);
    return terms.length ? e.replace(new RegExp(`(${terms.join('|')})`, 'gi'), '<strong class="highlight">$1</strong>') : e;
  };

  // ---------- facet definitions ----------------------------------------------
  const FLOW = [[0, 20, 'Under 20'], [20, 50, '20–50'], [50, 100, '50–100'], [100, 1e9, '100+']];
  const HEAD = [[0, 50, 'Under 50'], [50, 100, '50–100'], [100, 200, '100–200'], [200, 1e9, '200+']];
  const order = labels => (a, b) => labels.indexOf(a) - labels.indexOf(b);
  const FACETS = [
    { key: 'type', label: 'Category', get: p => p.type || null },
    { key: 'stock', label: 'Availability', get: p => (p.available ? 'In stock' : 'Call for availability'), sort: order(['In stock', 'Call for availability']) },
    { key: 'brand', label: 'Brand', get: p => p.brand || null },
    { key: 'hp', label: 'Horsepower', pills: true, get: p => p.hp || null, sort: (a, b) => hpNum(a) - hpNum(b) },
    { key: 'flow', label: 'Max flow (GPM)', get: p => bucket(p.flow, FLOW), sort: order(FLOW.map(b => b[2])) },
    { key: 'head', label: 'Max head (ft)', get: p => bucket(p.head, HEAD), sort: order(HEAD.map(b => b[2])) },
    { key: 'volt', label: 'Voltage', get: p => (p.volt ? `${p.volt} V` : null) },
    { key: 'phase', label: 'Phase', get: p => (!p.phase ? null : /^3/.test(p.phase) ? 'Three phase' : 'Single phase') },
    { key: 'pdf', label: 'Documents', get: p => (p.pdf ? 'Has spec sheet (PDF)' : null) },
  ];

  // ---------- state ------------------------------------------------------------
  let products = [];
  let loading = true;
  const state = { sel: {}, pmin: null, pmax: null, sort: '', page: 1, mode: cfg.mode || 'grid' };
  FACETS.forEach(f => { state.sel[f.key] = new Set(); });

  const readUrl = () => {
    const q = new URLSearchParams(location.search);
    FACETS.forEach(f => {
      const v = q.get(P + f.key);
      if (v) v.split('|').forEach(x => state.sel[f.key].add(x));
    });
    state.pmin = q.get(P + 'pmin') ? parseFloat(q.get(P + 'pmin')) : null;
    state.pmax = q.get(P + 'pmax') ? parseFloat(q.get(P + 'pmax')) : null;
    state.sort = q.get(P + 'sort') || '';
    state.page = parseInt(q.get(P + 'page'), 10) || 1;
    if (q.get(P + 'view')) state.mode = q.get(P + 'view');
  };
  const writeUrl = () => {
    const q = new URLSearchParams(location.search);
    [...q.keys()].filter(k => k.startsWith(P)).forEach(k => q.delete(k));
    FACETS.forEach(f => { if (state.sel[f.key].size) q.set(P + f.key, [...state.sel[f.key]].join('|')); });
    if (state.pmin !== null) q.set(P + 'pmin', state.pmin);
    if (state.pmax !== null) q.set(P + 'pmax', state.pmax);
    if (state.sort) q.set(P + 'sort', state.sort);
    if (state.page > 1) q.set(P + 'page', state.page);
    if (state.mode !== (cfg.mode || 'grid')) q.set(P + 'view', state.mode);
    const s = q.toString();
    history.replaceState(null, '', location.pathname + (s ? '?' + s : '') + location.hash);
  };

  // ---------- filtering ---------------------------------------------------------
  const priceActive = () => state.pmin !== null || state.pmax !== null;
  const passPrice = p => {
    if (!priceActive()) return true;
    if (p.cfp) return false;
    const dollars = p.price / 100;
    return (state.pmin === null || dollars >= state.pmin) && (state.pmax === null || dollars <= state.pmax);
  };
  const passFacets = (p, skipKey) => FACETS.every(f => f.key === skipKey || !state.sel[f.key].size || state.sel[f.key].has(f.get(p)));
  const matching = () => products.filter(p => passPrice(p) && passFacets(p));

  const SORTS = {
    'price-ascending': (a, b) => (a.cfp - b.cfp) || a.price - b.price,
    'price-descending': (a, b) => (a.cfp - b.cfp) || b.price - a.price,
    'title-ascending': (a, b) => a.title.localeCompare(b.title),
    'title-descending': (a, b) => b.title.localeCompare(a.title),
  };

  // ---------- rendering: facets ---------------------------------------------
  const renderFacets = () => {
    let html = '<h3>Refine results</h3>';
    FACETS.forEach(f => {
      const counts = new Map();
      let withValue = 0;
      products.forEach(p => {
        const v = f.get(p);
        if (v === null) return;
        withValue++;
        if (passPrice(p) && passFacets(p, f.key)) counts.set(v, (counts.get(v) || 0) + 1);
        else if (!counts.has(v)) counts.set(v, 0);
      });
      const values = [...counts.keys()].sort(f.sort || ((a, b) => String(a).localeCompare(String(b))));
      // Only show a facet that can narrow the results
      if (values.length < 2 && withValue === products.length && !state.sel[f.key].size) return;
      if (!values.length) return;
      html += `<div class="filter-group"><h3 style="margin-bottom:8px">${esc(f.label)}</h3>`;
      if (f.pills) html += '<div class="filter-pill-row">';
      values.forEach(v => {
        const on = state.sel[f.key].has(v);
        const n = counts.get(v);
        const off = !n && !on;
        const cls = [f.pills ? 'filter-pill' : '', f.pills && on ? 'on' : '', off ? 'is-empty' : ''].filter(Boolean).join(' ');
        html += `<label${cls ? ` class="${cls}"` : ''}><input type="checkbox" data-facet="${f.key}" value="${esc(v)}"${on ? ' checked' : ''}${off ? ' disabled' : ''}> ${esc(v)}${f.pills ? '' : `<span class="count">${n}</span>`}</label>`;
      });
      if (f.pills) html += '</div>';
      html += '</div>';
    });
    const anyPrice = products.some(p => !p.cfp && p.price > 0);
    if (anyPrice) {
      html += `<div class="filter-group"><h3 style="margin-bottom:8px">Price</h3><div class="price-range">
        <input type="number" min="0" step="any" data-price="min" placeholder="$ Min" aria-label="Minimum price" value="${state.pmin ?? ''}">
        <input type="number" min="0" step="any" data-price="max" placeholder="$ Max" aria-label="Maximum price" value="${state.pmax ?? ''}">
        </div><button type="button" class="filter-apply" data-price-apply>Apply</button>
        <div class="hint" style="font-size:11.5px;color:var(--ink-3);margin-top:6px">Call-for-price items are hidden when a price range is set.</div></div>`;
    }
    els.facets.innerHTML = html;
  };

  // ---------- rendering: chips --------------------------------------------------
  const renderChips = () => {
    if (!els.chips) return;
    const chips = [];
    FACETS.forEach(f => state.sel[f.key].forEach(v => chips.push(`<button type="button" class="active-chip" data-chip="${f.key}" data-value="${esc(v)}">${esc(f.key === 'hp' ? v + ' HP' : v)} <span aria-label="Remove">×</span></button>`)));
    if (priceActive()) chips.push(`<button type="button" class="active-chip" data-chip="price">${state.pmin !== null ? '$' + state.pmin : '$0'}–${state.pmax !== null ? '$' + state.pmax : 'any'} <span aria-label="Remove">×</span></button>`);
    els.chips.innerHTML = chips.length ? chips.join('') + '<button type="button" class="clear-all" data-clear>Clear all</button>' : '';
    els.chips.hidden = !chips.length;
  };

  // ---------- rendering: product cards -------------------------------------------
  const phoneHref = cfg.phoneHref || '';
  const specChips = p => [p.hp && `${p.hp} HP`, p.flow && `${p.flow} GPM`, p.head && `${p.head} ft`, p.volt && `${p.volt} V`].filter(Boolean);
  const gridCard = p => {
    const tags = (p.tags || []).map(t => t.toLowerCase());
    const badges = [p.available && '<span class="badge in-stock">In stock</span>', tags.includes('new') && '<span class="badge new">New</span>', tags.includes('popular') && '<span class="badge popular">Popular</span>'].filter(Boolean).join('');
    const title = p.title.replace(' — ', ' ');
    const pricing = p.cfp
      ? `<div class="cfp-text">Call for pricing</div><button type="button" class="add-btn outline" data-go="${esc(phoneHref)}">Quote</button>`
      : `<div class="price">${p.priceVaries ? '<small>From</small> ' : ''}${formatMoney(p.price, true)}</div><button type="button" class="add-btn" data-go="${esc(p.url)}">View</button>`;
    return `<a href="${esc(p.url)}" class="card-c">
      <div class="img">${p.img ? `<img src="${esc(p.img)}" alt="${esc(p.alt)}" loading="lazy">` : ''}<div class="badges">${badges}</div></div>
      <div class="meta">
        <div class="vendor">${esc(p.brand)}</div>
        <h3 class="ttl">${highlight(title.length > 60 ? title.slice(0, 57) + '...' : title)}</h3>
        ${p.pn ? `<div class="sku">${esc(p.pn)}</div>` : ''}
        <div class="quick-specs">${specChips(p).map(s => `<span>${esc(s)}</span>`).join('')}</div>
        <div class="pricing">${pricing}</div>
      </div></a>`;
  };
  const listRow = p => {
    const specs = [
      p.hp && ['HP', p.hp], p.flow && ['Max flow', `${p.flow} GPM`], p.head && ['Max head', `${p.head} ft`],
      p.volt && ['Voltage', `${p.volt} V`], p.phase && ['Phase', p.phase], p.suction && p.discharge && ['Ports', `${p.suction} × ${p.discharge}`],
    ].filter(Boolean);
    const specHtml = specs.length
      ? `<dl class="spec-dl">${specs.map(([k, v]) => `<div><dt>${esc(k)}</dt><dd>${esc(v)}</dd></div>`).join('')}</dl>`
      : (p.desc ? `<p class="r-desc">${esc(p.desc)}</p>` : '');
    const price = p.cfp
      ? '<div class="price cfp">Call for price</div><div class="unit">Manufacturer pricing policy</div>'
      : `<div class="price">${p.priceVaries ? '<small>From</small> ' : ''}${formatMoney(p.price, false)}</div><div class="unit">each</div>`;
    const stock = p.available ? '<div class="stock">✓ In stock · ships from Miami</div>' : '<div class="stock ds">Lead time applies · call for availability</div>';
    let action;
    if (p.cfp) action = `<a class="qty-btn outline" href="mailto:sales@miamipumpandsupply.com?subject=${encodeURIComponent('Quote request: ' + p.title)}">Request a quote</a>`;
    else if (p.single && p.variantAvailable) action = `<form method="post" action="/cart/add" class="qty-add"><input type="hidden" name="form_type" value="product"><input type="hidden" name="utf8" value="✓"><input type="hidden" name="id" value="${p.variant}"><input type="number" name="quantity" value="1" min="1" aria-label="Quantity for ${esc(p.title)}"><button type="submit">Add to cart</button></form>`;
    else if (p.available) action = `<a class="qty-btn" href="${esc(p.url)}">Choose options</a>`;
    else action = `<a class="qty-btn outline" href="${esc(phoneHref)}">Call ${esc(cfg.phone || '')}</a>`;
    return `<div class="r-row">
      <a href="${esc(p.url)}" class="thumb">${p.img ? `<img src="${esc(p.img.replace('width=500', 'width=260'))}" alt="${esc(p.alt)}" loading="lazy" width="120" height="120">` : ''}</a>
      <div class="r-info">
        <div class="vendor">${esc(p.brand)}</div>
        <a href="${esc(p.url)}" class="ttl">${highlight(p.title)}</a>
        ${p.pn ? `<div class="ids">Part # <b>${esc(p.pn)}</b></div>` : ''}
        ${specHtml}
        <div class="links">${p.pdf ? `<a href="${esc(p.pdf)}" target="_blank" rel="noopener"><span class="pdf-tag">PDF</span> Spec sheet</a>` : ''}<a href="${esc(p.url)}">View details ›</a></div>
      </div>
      <div class="buy">${price}${stock}${action}</div></div>`;
  };

  // ---------- rendering: results + pager ------------------------------------------
  const render = () => {
    let list = matching();
    if (SORTS[state.sort]) list = [...list].sort(SORTS[state.sort]);
    const pages = Math.max(1, Math.ceil(list.length / PER_PAGE));
    if (state.page > pages) state.page = pages;
    const start = (state.page - 1) * PER_PAGE;
    const pageItems = list.slice(start, start + PER_PAGE);

    if (!list.length) {
      els.results.className = 'mps-results-empty';
      els.results.innerHTML = `<div class="srch-empty" style="padding:40px 0"><h2>No products match these filters</h2><p><button type="button" class="filter-apply" data-clear>Clear all filters</button></p></div>`;
    } else if (state.mode === 'list') {
      els.results.className = 'r-list';
      els.results.innerHTML = pageItems.map(listRow).join('');
    } else {
      els.results.className = 'coll-grid';
      els.results.innerHTML = pageItems.map(gridCard).join('');
    }

    if (els.count) {
      const more = loading ? ' <span class="loading-more">· loading more…</span>' : '';
      els.count.innerHTML = list.length
        ? `Showing <strong>${start + 1}–${start + pageItems.length}</strong> of <strong>${list.length}</strong>${more}`
        : `<strong>0</strong> matches${more}`;
    }
    if (els.pager) {
      let h = '';
      if (pages > 1) {
        if (state.page > 1) h += `<button type="button" data-page="${state.page - 1}">‹ Prev</button>`;
        for (let i = 1; i <= pages; i++) {
          if (i === 1 || i === pages || Math.abs(i - state.page) <= 2) h += i === state.page ? `<span class="on" aria-current="page">${i}</span>` : `<button type="button" data-page="${i}">${i}</button>`;
          else if (Math.abs(i - state.page) === 3) h += '<span>…</span>';
        }
        if (state.page < pages) h += `<button type="button" data-page="${state.page + 1}">Next ›</button>`;
      }
      els.pager.innerHTML = h;
      els.pager.hidden = pages <= 1;
    }
    root.querySelectorAll('[data-mps-view]').forEach(b => b.classList.toggle('active', b.dataset.mpsView === state.mode));
    renderFacets();
    renderChips();
    writeUrl();
  };

  // ---------- events ---------------------------------------------------------------
  const update = (resetPage = true) => { if (resetPage) state.page = 1; render(); };
  els.facets.addEventListener('change', e => {
    const k = e.target.dataset.facet;
    if (!k) return;
    e.target.checked ? state.sel[k].add(e.target.value) : state.sel[k].delete(e.target.value);
    update();
  });
  const applyPrice = () => {
    const v = n => { const x = els.facets.querySelector(`[data-price="${n}"]`); return x && x.value !== '' ? parseFloat(x.value) : null; };
    state.pmin = v('min'); state.pmax = v('max');
    update();
  };
  els.facets.addEventListener('click', e => { if (e.target.closest('[data-price-apply]')) applyPrice(); });
  els.facets.addEventListener('keydown', e => { if (e.key === 'Enter' && e.target.dataset.price) { e.preventDefault(); applyPrice(); } });
  root.addEventListener('click', e => {
    const chip = e.target.closest('[data-chip]');
    if (chip) {
      if (chip.dataset.chip === 'price') { state.pmin = state.pmax = null; } else state.sel[chip.dataset.chip].delete(chip.dataset.value);
      return update();
    }
    if (e.target.closest('[data-clear]')) {
      FACETS.forEach(f => state.sel[f.key].clear()); state.pmin = state.pmax = null;
      return update();
    }
    const pg = e.target.closest('[data-page]');
    if (pg) { state.page = parseInt(pg.dataset.page, 10); render(); root.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    const go = e.target.closest('[data-go]');
    if (go) { e.preventDefault(); if (go.dataset.go) location.href = go.dataset.go; return; }
    const view = e.target.closest('[data-mps-view]');
    if (view) { state.mode = view.dataset.mpsView; render(); }
  });
  if (els.sort) {
    els.sort.onchange = null; // drop the server-side submit handler
    els.sort.addEventListener('change', () => { state.sort = els.sort.value; update(); });
  }

  // ---------- loading ---------------------------------------------------------------
  const feedUrl = page => {
    const u = new URL(location.href);
    [...u.searchParams.keys()].filter(k => k.startsWith(P) || k === 'page' || k === BATCH_PARAM).forEach(k => u.searchParams.delete(k));
    u.searchParams.set(BATCH_PARAM, 'mps-data');
    u.searchParams.set('page', page);
    return u.pathname + '?' + u.searchParams.toString();
  };
  const fetchBatch = async page => {
    // No JSON Accept header: Shopify would route it to its own JSON API instead of the theme template.
    const r = await fetch(feedUrl(page), { credentials: 'same-origin' });
    if (!r.ok) throw new Error(`feed ${page}: ${r.status}`);
    return JSON.parse(await r.text());
  };

  (async () => {
    readUrl();
    if (els.sort && state.sort) els.sort.value = state.sort;
    let first;
    try { first = await fetchBatch(1); } catch (err) {
      console.warn('[mps-filters] falling back to server results', err);
      root.classList.remove('mps-loading'); els.facets.hidden = true; els.facets.innerHTML = '';
      return;
    }
    products = first.products;
    loading = first.pages > 1;
    els.native.forEach(n => { n.hidden = true; });
    els.facets.hidden = false;
    root.classList.remove('mps-loading');
    root.classList.add('mps-js-ready');
    render();
    for (let page = 2; page <= first.pages; page++) {
      try { products = products.concat((await fetchBatch(page)).products); }
      catch (err) { console.warn('[mps-filters] stopped loading at batch', page, err); break; }
      if (page === first.pages) loading = false;
      render();
    }
    loading = false;
    render();
  })();
})();
